import { useEffect, useState, useRef } from 'react';
import Intercom from '@intercom/messenger-js-sdk';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface IntercomMessengerProps {
  appId: string;
}

export const IntercomMessenger = ({ appId }: IntercomMessengerProps) => {
  const { user } = useAuth();
  const [userHash, setUserHash] = useState<string | null>(null);
  const initializedWith = useRef<string | null>(null);
  const devLogShown = useRef(false);

  // Don't initialize in development unless explicitly enabled
  const isDev = import.meta.env.DEV;
  const enableInDev = import.meta.env.VITE_INTERCOM_ENABLE_DEV === 'true';

  if (isDev && !enableInDev) {
    if (!devLogShown.current) {
      console.log('[Intercom] Skipping initialization in development mode');
      devLogShown.current = true;
    }
    return null;
  }

  // Fetch hash for authenticated users
  useEffect(() => {
    if (!user) {
      setUserHash(null);
      return;
    }

    const fetchHash = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('generate-intercom-hash', {
          body: { userId: user.id },
        });

        if (!error && data?.userHash) {
          setUserHash(data.userHash);
        } else if (error) {
          // Gracefully handle 404 in development - Intercom will work without hash
          console.debug('[Intercom] Identity verification unavailable (expected in local dev)');
        }
      } catch (error) {
        // Don't log error if it's a 404 (expected locally)
        if (error?.message?.includes('404')) {
          console.debug('[Intercom] Hash generation not available locally (expected)');
        } else {
          console.error('[Intercom] Error fetching hash:', error);
        }
      }
    };

    fetchHash();
  }, [user?.id]);

  // Initialize Intercom
  useEffect(() => {
    // For authenticated users, MUST have hash before initializing
    if (user && !userHash) {
      return; // Wait for hash
    }

    // Don't re-initialize if already initialized with these exact credentials
    const initKey = user ? `${user.id}-${userHash}` : 'anonymous';
    if (initializedWith.current === initKey) {
      return;
    }

    const settings: any = {
      app_id: appId,
      action_color: '#4338ca',
      background_color: '#4338ca',
    };

    if (user && userHash) {
      settings.user_id = user.id;
      settings.email = user.email;
      settings.user_hash = userHash;

      if (user.user_metadata?.first_name || user.user_metadata?.last_name) {
        settings.name =
          `${user.user_metadata.first_name || ''} ${user.user_metadata.last_name || ''}`.trim();
      }

      if (user.created_at) {
        settings.created_at = Math.floor(new Date(user.created_at).getTime() / 1000);
      }
    }

    console.log('[Intercom] Initializing:', {
      user: user ? 'authenticated' : 'anonymous',
      has_hash: !!userHash,
    });

    try {
      Intercom(settings);
      initializedWith.current = initKey;
    } catch (error) {
      console.error('[Intercom] Failed:', error);
    }
  }, [appId, user, userHash]);

  return null;
};
